function decimalsForPrice(
  tokenPriceUSD: number | undefined,
  priceThresholdUSD: number,
): number | undefined {
  // Only use smart rounding if tokenPriceUSD is reasonable (between $0.0001 and $10M)
  // This prevents errors from bad backend data
  if (!tokenPriceUSD || tokenPriceUSD <= 0.0001 || tokenPriceUSD >= 10000000) {
    return undefined;
  }
  for (let d = 0; d <= 18; d++) {
    if (tokenPriceUSD * Math.pow(10, -d) < priceThresholdUSD) {
      return d;
    }
  }
  return undefined;
}

export function formatDisplayAmount(
  amount: string | bigint | number,
  tokenPriceUSD?: number,
  priceThresholdUSD = 0.05,
): string {
  const num = typeof amount === "string" ? parseFloat(amount) : Number(amount);

  if (isNaN(num) || num === 0) {
    return "0";
  }

  const decimalPlaces = decimalsForPrice(tokenPriceUSD, priceThresholdUSD) ?? 4;

  const multiplier = Math.pow(10, decimalPlaces);
  let formatted = (Math.floor(num * multiplier) / multiplier).toFixed(
    decimalPlaces,
  );

  formatted = formatted.replace(/\.?0+$/, "");

  if (num >= 10000) {
    formatted = Math.floor(num).toString();
  }

  if (Math.abs(num) >= 1000) {
    const parts = formatted.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    formatted = parts.join(".");
  }

  if (num < 0.0001 && !tokenPriceUSD) {
    formatted = "<0.0001";
  }

  return formatted;
}

// Rounds up so sending the displayed amount never leaves the user short again.
export function formatRemainingAmount(
  amount: string,
  tokenPriceUSD?: number,
): string {
  const num = parseFloat(amount);

  if (isNaN(num) || num <= 0) {
    return "0";
  }

  const decimalPlaces = Math.max(4, decimalsForPrice(tokenPriceUSD, 0.05) ?? 0);
  const multiplier = Math.pow(10, decimalPlaces);
  // The epsilon keeps float noise (e.g. 1.1 * 100 = 110.00000000000001) from adding a tick.
  const roundedUp = Math.ceil(num * multiplier - 1e-9) / multiplier;

  return roundedUp.toFixed(decimalPlaces).replace(/\.?0+$/, "");
}
